import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, ShoppingBag, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '@/lib/cart';
import type { CartItem as CartItemType } from '@/types';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

const FREE_SHIPPING_THRESHOLD = 50;

function CartItem({ item }: { item: CartItemType }) {
  const { updateQuantity, removeItem } = useCart();

  return (
    <div className="flex gap-4 py-4 border-b border-gray-100 dark:border-gray-800">
      <Link
        to={`/product/${item.slug}`}
        className="relative w-20 h-20 flex-shrink-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden"
      >
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </Link>

      <div className="flex-1 min-w-0">
        <Link
          to={`/product/${item.slug}`}
          className="text-sm font-medium line-clamp-2 hover:text-primary-600 transition-colors"
        >
          {item.name}
        </Link>

        <div className="mt-1 flex items-center justify-between">
          <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Decrease quantity"
              disabled={item.quantity <= 1}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="px-3 text-sm font-medium min-w-[2rem] text-center">
              {item.quantity}
            </span>
            <button
              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Increase quantity"
              disabled={item.quantity >= item.maxQuantity}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <span className="text-sm font-semibold">
            S${(item.price * item.quantity).toFixed(2)}
          </span>
        </div>

        {item.quantity >= item.maxQuantity && (
          <p className="mt-1 text-xs text-warning">Max stock reached</p>
        )}
      </div>

      <button
        onClick={() => removeItem(item.productId)}
        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors self-start"
        aria-label={`Remove ${item.name} from cart`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export function CartDrawer({ isOpen, onClose, onCheckout }: CartDrawerProps) {
  const { getSummary, clearCart } = useCart();
  const summary = getSummary();
  const freeShippingProgress = Math.min((summary.subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const remainingForFreeShipping = Math.max(FREE_SHIPPING_THRESHOLD - summary.subtotal, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 right-0 w-full max-w-md bg-white dark:bg-gray-900 z-50 flex flex-col shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Shopping cart"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Cart ({summary.itemCount})
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {summary.items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium mb-2">Your cart is empty</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Add some gadgets to get started!
                </p>
                <Link
                  to="/products"
                  onClick={onClose}
                  className="px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Shop Now
                </Link>
              </div>
            ) : (
              <>
                {summary.subtotal < FREE_SHIPPING_THRESHOLD && (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span>Free shipping progress</span>
                      <span className="font-medium">
                        S${remainingForFreeShipping.toFixed(2)} more
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-600 to-green-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${freeShippingProgress}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </div>
                )}

                {summary.subtotal >= FREE_SHIPPING_THRESHOLD && (
                  <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm text-center font-medium">
                    You've unlocked free shipping!
                  </div>
                )}

                <div className="flex-1 overflow-y-auto px-4">
                  {summary.items.map((item) => (
                    <CartItem key={item.productId} item={item} />
                  ))}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-800 p-4 space-y-4">
                  {summary.savings > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">You save</span>
                      <span className="text-green-600 font-medium">
                        -S${summary.savings.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-semibold">
                    <span>Subtotal</span>
                    <span>S${summary.subtotal.toFixed(2)}</span>
                  </div>

                  <button
                    onClick={onCheckout}
                    className="w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Checkout
                  </button>

                  <button
                    onClick={clearCart}
                    className="w-full py-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
                  >
                    Clear Cart
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
